import { PageContainer } from "@toolpad/core";
import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Avatar,
  Chip,
} from "@mui/material";

interface StreamCardProps {
  title: string;
  description: string;
  thumbnail: string;
  relativeTime: string;
}

const StreamCard: React.FC<StreamCardProps> = ({
  title,
  description,
  thumbnail,
  relativeTime,
}) => (
  <Card sx={{ width: 300, boxShadow: 3, borderRadius: 2 }}>
    <CardMedia component="img" height="140" image={thumbnail} alt={title} />
    <CardContent>
      <Typography gutterBottom variant="h6" component="div">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      <Chip
        label={`Live ${relativeTime}`}
        color="error"
        size="small"
        sx={{ marginTop: 1 }}
      />
    </CardContent>
  </Card>
);

interface TalentCardProps {
  name: string;
  streamsCount: number;
  profileImage: string;
}

const TalentCard: React.FC<TalentCardProps> = ({
  name,
  streamsCount,
  profileImage,
}) => (
  <Card
    sx={{
      width: 300,
      display: "flex",
      alignItems: "center",
      padding: 2,
      boxShadow: 3,
      borderRadius: 2,
    }}
  >
    <Avatar
      alt={name}
      src={profileImage}
      sx={{ width: 60, height: 60, marginRight: 2 }}
    />
    <Box>
      <Typography variant="h6" component="div">
        {name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {streamsCount} streams
      </Typography>
    </Box>
  </Card>
);

interface CoffeeCallCardProps {
  name: string;
  availableSlots: number;
  profileImage: string;
}

const CoffeeCallCard: React.FC<CoffeeCallCardProps> = ({
  name,
  availableSlots,
  profileImage,
}) => (
  <Card sx={{ width: 300, boxShadow: 3, borderRadius: 2, textAlign: "center" }}>
    <CardMedia
      component="img"
      height="140"
      image={profileImage}
      alt={name}
      sx={{ borderRadius: "50%", width: 100, height: 100, margin: "16px auto" }}
    />
    <CardContent>
      <Typography gutterBottom variant="h6" component="div">
        {name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {availableSlots} slots available
      </Typography>
      <Button variant="contained" color="primary" sx={{ marginTop: 2 }}>
        Book Now
      </Button>
    </CardContent>
  </Card>
);

export const TalentHomepage: React.FC = () => {
  return (
    <PageContainer>
      <Typography variant="h4" gutterBottom>
        Active Public Cards
      </Typography>
      <Grid container spacing={2}>
        {[
          {
            title: "Live Music with Alex",
            description: "Join Alex for a live acoustic session.",
            thumbnail:
              "https://static.vecteezy.com/system/resources/thumbnails/037/044/052/small/ai-generated-studio-shot-of-black-headphones-over-music-note-explosion-background-with-empty-space-for-text-photo.jpg",
            relativeTime: "2 minutes ago",
          },
          {
            title: "Cooking with Chef Jamie",
            description: "Learn to cook Italian dishes.",
            thumbnail:
              "https://thumbs.dreamstime.com/b/chef-cooking-delicious-food-kitchen-ai-generated-chef-cooking-delicious-food-kitchen-324515320.jpg",
            relativeTime: "15 minutes ago",
          },
          {
            title: "Yoga for Beginners",
            description: "Start your yoga journey with simple poses.",
            thumbnail:
              "https://img.freepik.com/premium-photo/woman-doing-yoga-beach-with-mountain-background_865967-25537.jpg",
            relativeTime: "30 minutes ago",
          },
          {
            title: "Gaming Marathon",
            description: "Watch an exciting gaming session.",
            thumbnail:
              "https://t4.ftcdn.net/jpg/05/64/31/67/360_F_564316725_zE8llusnCk3Sfr9rdfKya6fV7BQbjfyV.jpg",
            relativeTime: "1 hour ago",
          },
          {
            title: "Tech Talk Live",
            description: "Discussing the latest in technology.",
            thumbnail:
              "https://img.freepik.com/premium-photo/illustration-design-headphones-with-podcast-microphone_1151123-2816.jpg?semt=ais_hybrid",
            relativeTime: "2 hours ago",
          },
        ].map((stream, index) => (
          <Grid item key={index}>
            <StreamCard {...stream} />
          </Grid>
        ))}
      </Grid>

      <Typography variant="h4" gutterBottom sx={{ marginTop: 4 }}>
        Active Talents
      </Typography>
      <Grid container spacing={2}>
        {[
          {
            name: "Sophia Taylor",
            streamsCount: 25,
            profileImage: "https://picsum.photos/300/140?random=15",
          },
          {
            name: "Liam Johnson",
            streamsCount: 18,
            profileImage: "https://picsum.photos/300/140?random=16",
          },
          {
            name: "Olivia Brown",
            streamsCount: 30,
            profileImage: "https://picsum.photos/300/140?random=17",
          },
          {
            name: "Ethan Moore",
            streamsCount: 12,
            profileImage: "https://picsum.photos/300/140?random=18",
          },
          {
            name: "Charlotte White",
            streamsCount: 20,
            profileImage: "https://picsum.photos/300/140?random=19",
          },
        ].map((talent, index) => (
          <Grid item key={index}>
            <TalentCard {...talent} />
          </Grid>
        ))}
      </Grid>

      <Typography variant="h4" gutterBottom sx={{ marginTop: 4 }}>
        Coffee Calls
      </Typography>
      <Grid container spacing={2}>
        {[
          {
            name: "Emma Wilson",
            availableSlots: 2,
            profileImage: "https://picsum.photos/300/140?random=20",
          },
          {
            name: "James Davis",
            availableSlots: 4,
            profileImage: "https://picsum.photos/300/140?random=21",
          },
          {
            name: "Mia Martinez",
            availableSlots: 5,
            profileImage: "https://picsum.photos/300/140?random=22",
          },
          {
            name: "Noah Garcia",
            availableSlots: 1,
            profileImage: "https://picsum.photos/300/140?random=23",
          },
          {
            name: "Isabella Hernandez",
            availableSlots: 3,
            profileImage: "https://picsum.photos/300/140?random=24",
          },
        ].map((coffeeCall, index) => (
          <Grid item key={index}>
            <CoffeeCallCard {...coffeeCall} />
          </Grid>
        ))}
      </Grid>
    </PageContainer>
  );
};
